import { useEffect, useRef, useState } from 'react';
import { type DotLottie } from '@lottiefiles/dotlottie-web';
import { SplashScreenType } from '@/app/models/SplashScreenType';
import { DEFAULT_SPLASH_ANIMATION } from '@/app/consts/interfaceAppearance';
import SafeImage from '@/app/modules/widgets/SafeImage';
import './SplashScreenMedia.css';

type SplashScreenMediaProps = {
  source?: string;
  type: SplashScreenType;
  systemName?: string;
  onReady?: () => void;
};

export const SplashScreenMedia = ({ source, type, systemName, onReady }: SplashScreenMediaProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failedSource, setFailedSource] = useState<string>();
  const isAnimation = type === SplashScreenType.LOTTIE || !source;
  const animationSource = source || DEFAULT_SPLASH_ANIMATION;
  const animationFailed = isAnimation && failedSource === animationSource;
  const fallbackSource = '/media/logos/easypdv-icon.svg';
  const imageSource = source || fallbackSource;
  const imageAlt = systemName || 'Imagem da tela de carregamento';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!isAnimation || !canvas || animationFailed) return;
    let cancelled = false;
    let player: DotLottie | undefined;
    const showFallback = () => {
      if (cancelled) return;
      setFailedSource(animationSource);
      onReady?.();
    };
    void import('./playSplashAnimation')
      .then(({ playSplashAnimation }) => {
        if (cancelled) return;
        player = playSplashAnimation(canvas, animationSource, showFallback, onReady);
      })
      .catch(showFallback);
    return () => {
      cancelled = true;
      player?.destroy();
    };
  }, [isAnimation, animationSource, animationFailed, onReady]);

  if (isAnimation && !animationFailed)
    return (
      <div className="splash-media">
        <canvas ref={canvasRef} aria-label={imageAlt} height={220} width={360} />
      </div>
    );

  const displayedImage = animationFailed ? fallbackSource : imageSource;
  return (
    <div className="splash-media">
      <SafeImage alt={imageAlt} fallbackSrc={fallbackSource} src={displayedImage} onError={onReady} onLoad={onReady} />
      {animationFailed ? <small>Não foi possível carregar a animação.</small> : null}
    </div>
  );
};
